import { useTranslator } from "@eo-locale/react";

import { Box, Text, Contribution } from "@/storybook-components";
import { buildImgixUrl, stripEditorHtml } from "@/lib";
import { usePrivateFileRecordImageUrl } from "@/hooks/use-private-file-display-url";
import { useAppSelector } from "@/redux/store/hooks";
import { categoryMap } from "@/helpers/categories";
import ActionList from "./action-list";
import useBudget from "@/hooks/useBudget";

const PreviewCard = ({
  data,
  username,
  avatarURL,
  showCommentIcon = true
}): JSX.Element => {
  const { formatBudget } = useBudget();
  const { translate } = useTranslator();
  const { backgroundColours } = useAppSelector(
    (state) => state.toolCustomizations
  );
  const { brainstormer } = useAppSelector((state) => state.brainstormers);
  const { disableVoting, layout } = brainstormer;
  const unitCode = brainstormer?.baCategoryConfigAttributes?.unitCode || "";

  const categories = useAppSelector((state) => state.brainstormers.categories);
  const catMap = categoryMap(categories);

  const totalFund = formatBudget(
    data?.currentUserAllocation || data?.baItemConfigAttributes?.unitCap || 0,
    unitCode
  );

  const categoryName =
    data?.categoryId != null ? catMap[String(data.categoryId)] : undefined;

  const mediaImageUrl = usePrivateFileRecordImageUrl({
    record: data,
    tool: "ideas",
    height: 252,
    width: 448,
    fit: "min"
  });

  return (
    <Box display="flex" flexDirection="column" gap={4} pointerEvents="none">
      <Text fontSize="sm" fontWeight="semibold">
        {translate("common.preview")}
      </Text>
      <Contribution
        id="preview-card"
        cardDescription={stripEditorHtml(data.description) || ""}
        cardHeading={data.name}
        variant="vertical"
        commentsCount={0}
        contributionTime={translate("common.justNow")}
        imageAlt={null}
        imageSrc={mediaImageUrl || null}
        isThumbsUp={!disableVoting}
        showCommentIcon={showCommentIcon}
        isLoaded={true}
        shareText={translate("brainstormers.share")}
        showDescription
        showFooter
        showImage
        thumbsUpCount={0}
        tileBackgroundColor={
          data.backgroundColourId
            ? backgroundColours.find(
                ({ id }) => `${data.backgroundColourId}` === `${id}`
              )?.name
            : "gray.100"
        }
        showTags={!!categoryName}
        tags={categoryName ? [categoryName] : []}
        username={username || translate("common.user")}
        userAvatarSrc={buildImgixUrl(avatarURL, 40, 40, "min")}
        showMoreOptions
        actionList={<ActionList menuList={[{}]} currentData={null} />}
        showBudgeting={layout === "BudgetAllocator"}
        totalFund={totalFund}
        fundLocale={translate("budget.fundFor")}
        fundedForLocale={translate("budget.fundedFor")}
        undoBudgetingLocale={translate("budget.undo")}
      />
    </Box>
  );
};

export default PreviewCard;
